import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { ReviewsTypes } from '@frontend/api-reviews';
import { Chips } from '@frontend/chips';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useTimestampFormatter } from '@frontend/timestamp-formatter';
import { Avatar, styles, Text, useThemeValues, Chip, ListRow } from '@frontend/design-system';
import { SiteLogo } from '..';
import { Match } from '../../hooks';
import { ReviewRating } from '../review-rating/review-rating';

dayjs.extend(calendar);

type CompactReviewProps = {
  author: string;
  datePublished?: string;
  hasReplied?: boolean;
  onClick?: React.MouseEventHandler;
  matches?: readonly Match[];
  profileImage?: string;
  rating?: number;
  isSelected?: boolean;
  recommendation?: ReviewsTypes.Recommendation | string;
  review?: string;
  showLogo?: boolean;
  showNotificationBubble?: boolean;
  locationName?: string | null;
  siteName: ReviewsTypes.SiteName | string;
  size?: 'medium' | 'small';
};

export const CompactReview = ({
  author,
  datePublished,
  hasReplied,
  matches,
  profileImage,
  rating,
  recommendation,
  review,
  isSelected,
  showLogo,
  showNotificationBubble,
  locationName,
  siteName,
  size = 'medium',
  ...rest
}: CompactReviewProps) => {
  const { t } = useTranslation('reviews');
  const theme = useThemeValues();
  const showDetails = rating || recommendation || hasReplied || locationName;
  const isPrivateReview = siteName === ReviewsTypes.SiteName.Private;
  const { selectedLocationIds } = useAppScopeStore();

  const authorDisplayName = useMemo(() => {
    const authorNameTrimmed = author?.trim();
    if (authorNameTrimmed) return authorNameTrimmed;

    switch (siteName) {
      case ReviewsTypes.SiteName.Facebook:
        return t('Facebook User');
      case ReviewsTypes.SiteName.Google:
        return t('Google User');
      default:
        return t('Weave User');
    }
  }, [siteName, author]);

  return (
    <ListRow
      as='article'
      isSelected={isSelected}
      css={{
        gridTemplateColumns: 'auto 1fr',
        padding: theme.spacing(3),
        '&:hover, &:focus, &:focus-within': {
          '& .location-chip': {
            backgroundColor: theme.colors.white,
          },
        },
      }}
      {...rest}
      tabIndex={0}
    >
      <ListRow.Lead>
        <Avatar name={authorDisplayName} size={size} src={profileImage}>
          {showNotificationBubble && size === 'medium' && <Avatar.Dot variant='primary' />}
          {showLogo && (
            <Avatar.BottomIcon>
              <SiteLogo
                site={siteName as ReviewsTypes.SiteName}
                size={22}
                css={css`
                  padding: 2px;
                  background: white;
                `}
              />
            </Avatar.BottomIcon>
          )}
        </Avatar>
      </ListRow.Lead>
      <ListRow.Content>
        {!isPrivateReview && (
          <section
            css={[
              listRowHeaderStyle,
              css`
                margin-bottom: ${theme.spacing(0.5)};
              `,
            ]}
          >
            <ReviewRating
              siteName={siteName as ReviewsTypes.SiteName}
              rating={rating}
              recommendation={recommendation as ReviewsTypes.Recommendation}
            />
            <DateText datePublished={datePublished} />
          </section>
        )}
        <section
          css={[
            listRowHeaderStyle,
            css`
              margin-bottom: ${theme.spacing(0.5)};
            `,
          ]}
        >
          <ListRow.Content.Title isBold={true} css={styles.truncate}>
            {authorDisplayName}
          </ListRow.Content.Title>
          {isPrivateReview && <DateText datePublished={datePublished} />}
        </section>
        {review?.trim() ? (
          <ListRow.Content.Subtitle
            css={css`
              margin: 0;
            `}
          >
            {review}
          </ListRow.Content.Subtitle>
        ) : null}
        {showDetails && (
          <ListRow.Content.TertiaryTitle
            css={css`
              display: flex;
              align-items: center;
              gap: ${theme.spacing(1)};
              margin-top: ${theme.spacing(1)};
            `}
            as='span'
          >
            {locationName && selectedLocationIds.length > 1 && (
              <Chips.LocationChip
                css={css`
                  max-width: 200px;
                `}
                className='location-chip'
              >
                {locationName}
              </Chips.LocationChip>
            )}
            {hasReplied && <Chip variant='success'>{t('Replied')}</Chip>}
          </ListRow.Content.TertiaryTitle>
        )}
      </ListRow.Content>
    </ListRow>
  );
};

const DateText = ({ datePublished }: Pick<CompactReviewProps, 'datePublished'>) => {
  const formatTimestamp = useTimestampFormatter();

  if (datePublished) {
    return (
      <Text
        size='small'
        color='light'
        css={css`
          margin: 0;
        `}
      >
        {formatTimestamp(datePublished)}
      </Text>
    );
  }
  return null;
};

const listRowHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 0;
`;
